import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../config/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [pendingPasswordChange, setPendingPasswordChange] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!token || pendingPasswordChange) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (error) {
      console.error("Auth error:", error);
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, pendingPasswordChange]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, must_change_password, requires_2fa, temp_token, email: userEmail } = response.data;
    
    // If 2FA is required, return immediately without storing token
    if (requires_2fa) {
      return { requires_2fa: true, temp_token, email: userEmail };
    }
    
    if (must_change_password) {
      // Set flag to prevent auto-fetch
      setPendingPasswordChange(true);
    }
    
    localStorage.setItem("token", newToken);
    setToken(newToken);
    
    // Don't set user if they must change password - this prevents auto-navigation
    if (!must_change_password) {
      setUser(userData);
    }
    
    return { user: userData, must_change_password, token: newToken, requires_2fa: false };
  };
  
  const setUserData = (userData) => {
    setPendingPasswordChange(false);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` },
  });

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, token, getAuthHeader, refreshUser, setUserData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
